/**
 * @fileoverview Styles for Custom Image Dialog
 *
 * Styles for the custom image dialog component that replaces
 * MDXEditor's default image dialog.
 */

/* ============================================================================
   DIALOG BACKDROP & CONTAINER
   ============================================================================ */

.wn-image-dialog-backdrop {
  position: fixed;
  inset: 0;
  z-index: var(--wn-z-dialog);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--wn-backdrop);
  backdrop-filter: blur(2px);
}

.wn-image-dialog {
  width: 100%;
  max-width: 400px;
  margin: var(--wn-space-5);
  background-color: var(--wn-zinc-900);
  border: 1px solid var(--wn-zinc-700);
  border-radius: var(--wn-radius-lg);
  box-shadow:
    0 20px 25px -5px var(--wn-backdrop-light),
    0 8px 10px -6px var(--wn-overlay-light-10);
}

/* ============================================================================
   DIALOG HEADER
   ============================================================================ */

.wn-image-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--wn-space-5) var(--wn-space-5) var(--wn-space-4);
  border-bottom: 1px solid var(--wn-zinc-800);
}

.wn-image-dialog-title {
  font-size: var(--wn-font-md);
  font-weight: 600;
  color: var(--wn-zinc-100);
  margin: 0;
}

.wn-image-dialog-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--wn-icon-btn-md);
  height: var(--wn-icon-btn-md);
  padding: 0;
  background: transparent;
  border: none;
  border-radius: var(--wn-radius-sm);
  color: var(--wn-zinc-400);
  cursor: pointer;
  transition:
    background-color var(--wn-transition-fast),
    color var(--wn-transition-fast);
}

.wn-image-dialog-close:hover {
  background-color: var(--wn-zinc-800);
  color: var(--wn-zinc-100);
}

/* ============================================================================
   TABS
   ============================================================================ */

.wn-image-dialog-tabs {
  display: flex;
  margin: var(--wn-space-4) var(--wn-space-5) 0;
  padding: var(--wn-space-1);
  background-color: var(--wn-zinc-800);
  border-radius: var(--wn-radius-md);
}

.wn-image-dialog-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--wn-space-3);
  padding: var(--wn-space-3) var(--wn-space-4);
  font-size: var(--wn-font-base);
  font-weight: 500;
  background: transparent;
  border: none;
  border-radius: var(--wn-radius-sm);
  color: var(--wn-zinc-400);
  cursor: pointer;
  transition:
    background-color var(--wn-transition-fast),
    color var(--wn-transition-fast);
}

.wn-image-dialog-tab:hover {
  color: var(--wn-zinc-200);
}

.wn-image-dialog-tab--active {
  background-color: var(--wn-zinc-700);
  color: var(--wn-zinc-100);
  box-shadow: var(--wn-shadow-sm);
}

/* ============================================================================
   CONTENT
   ============================================================================ */

.wn-image-dialog-content {
  display: flex;
  flex-direction: column;
  gap: var(--wn-space-4);
  padding: var(--wn-space-5);
}

/* ============================================================================
   DROPZONE
   ============================================================================ */

.wn-image-dialog-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--wn-space-3);
  padding: var(--wn-space-7);
  border: 2px dashed var(--wn-zinc-700);
  border-radius: var(--wn-radius-lg);
  cursor: pointer;
  transition:
    background-color var(--wn-transition-fast),
    border-color var(--wn-transition-fast);
}

.wn-image-dialog-dropzone:hover {
  background-color: var(--wn-overlay-3);
  border-color: var(--wn-zinc-600);
}

.wn-image-dialog-dropzone:focus {
  outline: 2px solid var(--wn-brand-500);
  outline-offset: 2px;
}

.wn-image-dialog-file-input {
  display: none;
}

.wn-image-dialog-dropzone-icon {
  color: var(--wn-zinc-500);
}

.wn-image-dialog-dropzone-text {
  font-size: var(--wn-font-base);
  color: var(--wn-zinc-400);
  margin: 0;
}

.wn-image-dialog-file-info {
  text-align: center;
}

.wn-image-dialog-file-name {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--wn-font-base);
  font-weight: 500;
  color: var(--wn-zinc-100);
  margin: 0;
}

.wn-image-dialog-file-size {
  font-size: var(--wn-font-xs);
  color: var(--wn-zinc-400);
  margin: var(--wn-space-1) 0 0;
}

/* ============================================================================
   FORM FIELDS
   ============================================================================ */

.wn-image-dialog-field {
  display: flex;
  flex-direction: column;
  gap: var(--wn-space-2);
}

.wn-image-dialog-label {
  font-size: var(--wn-font-base);
  font-weight: 500;
  color: var(--wn-zinc-300);
}

.wn-image-dialog-input {
  width: 100%;
  padding: var(--wn-space-3) var(--wn-space-4);
  font-size: var(--wn-font-base);
  font-family: inherit;
  background-color: var(--wn-zinc-800);
  border: 1px solid var(--wn-zinc-700);
  border-radius: var(--wn-radius-md);
  color: var(--wn-zinc-100);
  transition:
    border-color var(--wn-transition-fast),
    box-shadow var(--wn-transition-fast);
}

.wn-image-dialog-input::placeholder {
  color: var(--wn-zinc-500);
}

.wn-image-dialog-input:focus {
  outline: none;
  border-color: var(--wn-brand-500);
  box-shadow: 0 0 0 2px var(--wn-brand-alpha-20);
}

.wn-image-dialog-input--error {
  border-color: var(--wn-error-500);
}

.wn-image-dialog-input--error:focus {
  border-color: var(--wn-error-500);
  box-shadow: 0 0 0 2px var(--wn-error-alpha-20);
}

.wn-image-dialog-error {
  font-size: var(--wn-font-xs);
  color: var(--wn-error-500);
  margin: 0;
}

/* ============================================================================
   FOOTER
   ============================================================================ */

.wn-image-dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--wn-space-3);
  padding: var(--wn-space-4) var(--wn-space-5) var(--wn-space-5);
  border-top: 1px solid var(--wn-zinc-800);
}

/* ============================================================================
   LIGHT MODE OVERRIDES
   ============================================================================ */

.wn-light .wn-image-dialog {
  background-color: white;
  border-color: var(--wn-zinc-200);
}

.wn-light .wn-image-dialog-header {
  border-bottom-color: var(--wn-zinc-200);
}

.wn-light .wn-image-dialog-title {
  color: var(--wn-zinc-900);
}

.wn-light .wn-image-dialog-close {
  color: var(--wn-zinc-500);
}

.wn-light .wn-image-dialog-close:hover {
  background-color: var(--wn-zinc-100);
  color: var(--wn-zinc-900);
}

.wn-light .wn-image-dialog-tabs {
  background-color: var(--wn-zinc-100);
}

.wn-light .wn-image-dialog-tab {
  color: var(--wn-zinc-500);
}

.wn-light .wn-image-dialog-tab:hover {
  color: var(--wn-zinc-700);
}

.wn-light .wn-image-dialog-tab--active {
  background-color: white;
  color: var(--wn-zinc-900);
  box-shadow: var(--wn-shadow-sm);
}

.wn-light .wn-image-dialog-dropzone {
  border-color: var(--wn-zinc-300);
}

.wn-light .wn-image-dialog-dropzone:hover {
  background-color: var(--wn-zinc-50);
  border-color: var(--wn-zinc-400);
}

.wn-light .wn-image-dialog-dropzone-icon {
  color: var(--wn-zinc-400);
}

.wn-light .wn-image-dialog-dropzone-text {
  color: var(--wn-zinc-500);
}

.wn-light .wn-image-dialog-file-name {
  color: var(--wn-zinc-900);
}

.wn-light .wn-image-dialog-file-size {
  color: var(--wn-zinc-500);
}

.wn-light .wn-image-dialog-label {
  color: var(--wn-zinc-700);
}

.wn-light .wn-image-dialog-input {
  background-color: white;
  border-color: var(--wn-zinc-300);
  color: var(--wn-zinc-900);
}

.wn-light .wn-image-dialog-input::placeholder {
  color: var(--wn-zinc-400);
}

.wn-light .wn-image-dialog-footer {
  border-top-color: var(--wn-zinc-200);
}
